import type { ComponentProps } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@ifixit/ui/lib/utils';

export function Tabs({ className, ...props }: ComponentProps<typeof TabsPrimitive.Root>) {
   return <TabsPrimitive.Root data-slot="tabs" className={className} {...props} />;
}

/**
 * `scroll-fade-x` makes the strip its own scroller, fading the end that still
 * hides a trigger. The fade lives here rather than on a wrapper the page
 * supplies, so a strip cannot scroll without one, and never on the Tabs root,
 * whose full height would fade the panels too.
 *
 * `flex`, not `inline-flex`: an inline box sizes to its triggers, which lets a
 * row overrun a narrow column with nothing left for the scroller to scroll,
 * and forces a stretched strip to name a width that fights the gutters. A
 * block-level box takes the space available instead, gutters included. As a
 * flex item it is blockified either way, so rows beside a title are unaffected.
 *
 * `py-1` is the room the triggers' focus ring paints into, which a scroll
 * container otherwise clips. Equal on both sides: a strip beside a title is
 * centered as a box, so padding on one side only drops its labels off that
 * line by half the difference.
 */
export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
   return (
      <TabsPrimitive.List
         data-slot="tabs-list"
         className={cn('scroll-fade-x flex items-center gap-2 py-1', className)}
         {...props}
      />
   );
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
   return (
      <TabsPrimitive.Trigger
         data-slot="tabs-trigger"
         className={cn(
            "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-2 py-1 text-sm font-medium text-gray-900 transition-colors hover:text-brand-500 active:bg-gray-200 focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-gray-200 data-[state=active]:text-current [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
            className
         )}
         {...props}
      />
   );
}

export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
   return (
      <TabsPrimitive.Content
         data-slot="tabs-content"
         className={cn(
            'mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className
         )}
         {...props}
      />
   );
}
